import { useState, useEffect, useCallback } from 'react';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { firebaseApp } from '@/lib/firebase';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VAPID key from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = 'BPvuToyMXFkF1iOq_x1fs6KxtsCBe7xqrIg6_Zz61v96aQFcubzAw8Z6r9tb-PGHJZeQQCcGs-maE9Xa2vfTrAk';

export const usePushNotifications = (token) => {
  const [fcmToken, setFcmToken] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);

  const registerToken = useCallback(async (fcmToken) => {
    if (!token || !fcmToken) return;
    
    try {
      await axios.post(`${API}/fcm/register-token`, 
        { token: fcmToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('FCM token registered successfully');
    } catch (error) {
      console.error('Error registering FCM token:', error);
    }
  }, [token]);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === 'granted') {
        const messaging = getMessaging(firebaseApp);
        const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY });
        
        if (currentToken) {
          setFcmToken(currentToken);
          await registerToken(currentToken);
          return currentToken;
        }
      }
      return null;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  }, [registerToken]);

  // Listen for foreground messages
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    try {
      const messaging = getMessaging(firebaseApp);
      
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log('Foreground message received:', payload);
        
        // Show toast notification for foreground messages
        toast(payload.notification?.title || 'Nueva notificación', {
          description: payload.notification?.body,
          action: payload.data?.link ? {
            label: 'Ver',
            onClick: () => window.location.href = payload.data.link
          } : undefined
        });
      });

      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    } catch (error) {
      console.log('Firebase messaging not available:', error);
    }
  }, []);

  // Auto-register token when user is authenticated
  useEffect(() => {
    if (token && notificationPermission === 'granted' && !fcmToken) {
      requestPermission();
    }
  }, [token, notificationPermission, fcmToken, requestPermission]);

  return {
    fcmToken,
    notificationPermission,
    requestPermission,
    isSupported: 'Notification' in window && 'serviceWorker' in navigator
  };
};

export default usePushNotifications;
